/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import styled from '@emotion/styled';

/* ---------- prop types ---------- */

interface ToggleProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (value: boolean) => void;
  disabled?: boolean;
  id?: string;
  ariaLabel?: string;
  onColor?: string;
  offColor?: string;
  knobColor?: string;
}

interface TrackProps {
  $on: boolean;
  $onColor: string;
  $offColor: string;
}

interface KnobProps {
  $on: boolean;
  $knobColor: string;
}

/* ---------- styled components ---------- */

const Track = styled.button<TrackProps>`
  position: relative;
  display: inline-flex;
  align-items: center;

  width: 44px;
  height: 28px;

  padding: 0;
  border: none;
  border-radius: 9999px;
  outline: none;

  /* mobile + native resets */
  appearance: none;
  -webkit-appearance: none;
  -webkit-tap-highlight-color: transparent;

  /* keep rounded clipping consistent during tap/focus */
  overflow: hidden;

  background: ${({ $on, $onColor, $offColor }) => ($on ? $onColor : $offColor)};
  cursor: pointer;
  transition: background 180ms ease;

  &:disabled {
    cursor: not-allowed;
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(94, 132, 237, 0.45);
  }
`;

const Knob = styled.span<KnobProps>`
  position: absolute;
  top: 4px; /* (28 - 20) / 2 */
  left: 6px; /* 6px inset when OFF */

  width: 20px;
  height: 20px;

  border-radius: 50%;
  background: ${({ $knobColor }) => $knobColor};
  pointer-events: none;

  /* when ON, left should be 18px to keep 6px right gap => translateX(12px) */
  transform: ${({ $on }) => ($on ? 'translateX(12px)' : 'translateX(0)')};
  transition: transform 180ms ease;
`;

/* ---------- component ---------- */

export const ToggleSwitch: React.FC<ToggleProps> = ({
  checked,
  defaultChecked = true,
  onChange,
  disabled,
  id,
  ariaLabel,
  onColor = '#5E84ED',
  offColor = '#2B2D33',
  knobColor = '#E9ECF5',
}) => {
  // controlled/uncontrolled
  const [internal, setInternal] = useState(defaultChecked);
  const isControlled = typeof checked === 'boolean';
  const on = isControlled ? checked : internal;

  // interactions
  const toggle = () => {
    if (disabled) return;
    const next = !on;
    if (!isControlled) setInternal(next);
    onChange?.(next);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  // render
  return (
    <Track
      id={id}
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      disabled={disabled}
      $on={on}
      $onColor={onColor}
      $offColor={offColor}
      onClick={toggle}
      onKeyDown={onKeyDown}
    >
      <Knob $on={on} $knobColor={knobColor} />
    </Track>
  );
};

export default ToggleSwitch;
